import { useState, useEffect } from 'react';
import { isValidStorageId } from '../utils/validation';

/**
 * Hook personalizado para obtener valores de localStorage de forma segura
 * @param {string} key - Clave del localStorage
 * @param {*} defaultValue - Valor por defecto si no existe
 * @returns {*} - Valor del localStorage o defaultValue
 */
export const useLocalStorage = (key, defaultValue = null) => {
  const [value, setValue] = useState(() => {
    const storedValue = localStorage.getItem(key);
    // Verificar si el valor es válido antes de retornarlo
    if (!storedValue || storedValue === 'null' || storedValue === 'undefined') {
      return defaultValue;
    }
    return storedValue;
  });

  useEffect(() => {
    const handleStorageChange = () => {
      const newValue = localStorage.getItem(key);
      // Verificar si el valor es válido antes de establecerlo
      if (!newValue || newValue === 'null' || newValue === 'undefined') {
        setValue(defaultValue);
      } else {
        setValue(newValue);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [key, defaultValue]);

  return value;
};

/**
 * Hook para obtener el ID del usuario de forma segura
 * @returns {string|null} - ID del usuario o null
 */
export const useUserId = () => {
  return useLocalStorage('id');
};

/**
 * Hook para obtener el rol del usuario
 * @returns {string|null} - Rol del usuario o null
 */
export const useUserRole = () => {
  return useLocalStorage('rol');
};

/**
 * Hook para obtener el ID del empleado
 * @returns {string|null} - ID del empleado o null
 */
export const useEmployeeId = () => {
  return useLocalStorage('employeeId');
};

/**
 * Hook para verificar si el usuario es una empresa
 * @returns {boolean} - true si es empresa (rol 1)
 */
export const useIsCompany = () => {
  const rol = useUserRole();
  return rol === '1' || rol === 1;
};

/**
 * Hook para obtener datos del usuario de forma segura
 * Solo retorna si el ID es válido
 * @returns {{userId: string|null, isValid: boolean}}
 */
export const useValidUserId = () => {
  const userId = useUserId();
  const isValid = isValidStorageId(userId);
  
  return {
    userId: isValid ? userId : null,
    isValid
  };
};
